import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Eye, FileText, View, ViewIcon } from 'lucide-react';
import { Pagination } from 'antd';
import { getUserDocuments } from '@/api/User';
import { UserDocumentAPI } from '@/api/type';

interface Contract {
  contract_info: {
    contract_code: string;
    file_name: string;
  } | null;
  contract_file_url: string | null;
  created_at: string;
}

interface DocumentsTabProps {
  docs?: Contract[];
  currentPage?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
}

const PAGE_SIZE = 10;

const mapDocumentToContract = (doc: UserDocumentAPI): Contract => ({
  contract_info: doc.contract_info
    ? {
        contract_code: doc.contract_info.contract_code,
        file_name: doc.contract_info.file_name,
      }
    : null,
  contract_file_url: doc.contract_file_url || null,
  created_at: doc.created_at,
});

const getUserIdFromStorage = (): string | null => {
  const userData = localStorage.getItem('user');
  if (!userData) {
    return null;
  }

  try {
    const user = JSON.parse(userData);
    const userId = user?.id ?? user?.info?.id;
    return userId ? String(userId) : null;
  } catch (error) {
    console.error('Failed to parse user from localStorage:', error);
    return null;
  }
};

const DocumentsTab: React.FC<DocumentsTabProps> = ({
  docs,
  currentPage = 1,
  totalItems = 0,
  onPageChange
}) => {
  const { t } = useTranslation();
  const [internalDocs, setInternalDocs] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);

  useEffect(() => {
    if (docs) {
      return;
    }

    const fetchDocuments = async () => {
      const userId = getUserIdFromStorage();
      if (!userId) {
        setInternalDocs([]);
        return;
      }

      try {
        setIsLoading(true);
        const response = await getUserDocuments(userId);
        setInternalDocs(response.map(mapDocumentToContract));
      } catch (error) {
        console.error('Failed to load documents:', error);
        setInternalDocs([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocuments();
  }, [docs]);

  const sourceContracts: Contract[] = docs || internalDocs;

  const activePage = onPageChange ? currentPage : internalCurrentPage;
  const effectiveTotalItems = onPageChange ? totalItems : sourceContracts.length;

  const contracts = useMemo(() => {
    if (onPageChange) {
      return sourceContracts;
    }

    const start = (activePage - 1) * PAGE_SIZE;
    return sourceContracts.slice(start, start + PAGE_SIZE);
  }, [activePage, onPageChange, sourceContracts]);

  const handleView = (contract: Contract) => {
    if (contract.contract_file_url) {
      window.open(contract.contract_file_url, '_blank');
    }
  };

  const handleDownload = (contract: Contract) => {
    if (contract.contract_file_url) {
      const link = document.createElement('a');
      link.href = contract.contract_file_url;
      link.download = contract.contract_info?.file_name || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePageChange = (page: number) => {
    if (onPageChange) {
      onPageChange(page);
      return;
    }

    setInternalCurrentPage(page);
  };

  return (
    <div className="space-y-4">
      {/* Title */}
      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
        {t('profile.documents.title', 'Danh sách hợp đồng')}
      </h3>

      {/* Contracts List */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[500px]">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('profile.documents.code', 'Mã HĐ')}
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('profile.documents.name', 'Tên')}
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('profile.documents.signDate', 'Ngày ký')}
              </th>
              <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('profile.documents.download', 'Tải xuống')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {isLoading ? (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {t('common.loading', 'Đang tải...')}
                </td>
              </tr>
            ) : contracts.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {t('profile.documents.empty', 'Chưa có hợp đồng nào')}
                </td>
              </tr>
            ) : (
              contracts.map((contract, index) => (
                <tr key={contract.contract_info?.contract_code || index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-3 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap" title={contract.contract_info?.contract_code}>
                    {contract.contract_info?.contract_code
                      ? (contract.contract_info.contract_code.length > 20
                          ? contract.contract_info.contract_code.slice(0, 20) + '...'
                          : contract.contract_info.contract_code)
                      : 'N/A'}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-400" />
                      {contract.contract_info?.file_name || 'N/A'}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {contract.created_at}
                  </td>
                  <td className="px-3 py-3 text-center">
                    {/* <button 
                      disabled={!contract.contract_file_url}
                      onClick={() => handleView(contract)}
                      className="inline-flex items-center disabled:opacity-50 disabled:!cursor-not-allowed gap-1 px-3 py-1.5 text-sm font-medium text-yellow-600 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-md transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                    </button> */}
                    <button
                      disabled={!contract.contract_file_url}
                      onClick={() => handleDownload(contract)}
                      className="inline-flex items-center disabled:opacity-50 disabled:!cursor-not-allowed gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {effectiveTotalItems > PAGE_SIZE && (
        <div className="flex justify-end pt-4">
          <Pagination
            current={activePage}
            total={effectiveTotalItems}
            pageSize={PAGE_SIZE}
            onChange={handlePageChange}
            showSizeChanger={false}
          />
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;
